import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ArrowRight, CheckCircle2, Loader2, ExternalLink, Info, ChevronDown,
    ChevronRight, RotateCw, Clock,
} from "lucide-react";
import OnboardingShell from "./OnboardingShell";
import { api } from "@/lib/api";

// Screenshot showing where the Customer ID is located in Google Ads UI (top-right).
const CUSTOMER_ID_SCREENSHOT =
    "https://customer-assets.emergentagent.com/job_campaign-genius-39/artifacts/z4kt7tl0_uniqcustid.jpg";

export default function Step1GoogleAds() {
    const navigate = useNavigate();
    const [customerId, setCustomerId] = useState("");
    const [savedCustomerId, setSavedCustomerId] = useState("");
    const [consent, setConsent] = useState(false);
    const [saving, setSaving] = useState(false);
    const [status, setStatus] = useState("idle"); // idle | pending_manual_invitation | linked
    const [error, setError] = useState("");
    const [helpOpen, setHelpOpen] = useState(false);
    const [checking, setChecking] = useState(false);
    const pollRef = useRef(null);

    // Load existing state from backend.
    useEffect(() => {
        (async () => {
            try {
                const r = await api.get("/google-ads");
                if (r.data?.customer_id) {
                    const d = r.data.customer_id;
                    setSavedCustomerId(d);
                    setCustomerId(formatId(d));
                    setStatus(r.data.status || "pending_manual_invitation");
                    setConsent(true);
                }
            } catch {}
        })();
    }, []);

    // Auto-poll link status every 30s while pending (frontend convenience; cron does the heavy lifting).
    useEffect(() => {
        if (status !== "pending_manual_invitation" || !savedCustomerId) return;
        pollRef.current = setInterval(async () => {
            try {
                const r = await api.post("/google-ads/check-link");
                if (r.data?.linked) {
                    setStatus("linked");
                }
            } catch {}
        }, 30_000);
        return () => clearInterval(pollRef.current);
    }, [status, savedCustomerId]);

    const handleChange = (v) => {
        setCustomerId(formatId(v));
    };

    const validDigits = customerId.replace(/\D/g, "");
    const valid = validDigits.length === 10;

    const handleSubmit = async () => {
        setError("");
        setSaving(true);
        try {
            const r = await api.post("/google-ads", { customer_id: validDigits });
            setSavedCustomerId(r.data?.customer_id || validDigits);
            setStatus(r.data?.status || "pending_manual_invitation");
        } catch (e) {
            setError(e?.response?.data?.detail || "Помилка збереження");
        } finally {
            setSaving(false);
        }
    };

    const handleCheckNow = async () => {
        setChecking(true);
        setError("");
        try {
            const r = await api.post("/google-ads/check-link");
            if (r.data?.linked) {
                setStatus("linked");
            } else {
                setError("Запит ще не прийнято. Перевірте у Google Ads, що ви натиснули «Прийняти» — і спробуйте ще раз через хвилину.");
            }
        } catch (e) {
            setError(e?.response?.data?.detail || "Не вдалося перевірити статус. Спробуйте пізніше.");
        } finally {
            setChecking(false);
        }
    };

    const handleContinue = async () => {
        await api.post("/onboarding/step", { step: "2" });
        navigate("/onboarding/step-2");
    };

    const handleSkip = async () => {
        const ok = window.confirm(
            "Без підключення Google Ads будуть недоступні: автоматичний моніторинг, " +
            "тижневі звіти та аудит існуючих кампаній.\n\n" +
            "Ви зможете додати акаунт пізніше з налаштувань. Продовжити?"
        );
        if (!ok) return;
        await api.post("/onboarding/step", { step: "2", google_ads_skipped: true });
        navigate("/onboarding/step-2");
    };

    const showInviteBlock = !!savedCustomerId && status !== "expired" && status !== "rejected_conflict";
    const canContinue = status === "linked";
    const isProblemStatus = status === "expired" || status === "rejected_conflict";

    return (
        <OnboardingShell current={1}>
            <div className="max-w-[820px] mx-auto" data-testid="step1-root">
                <h1 className="font-display font-extrabold text-text-primary" style={{ fontSize: "clamp(28px, 4vw, 44px)", letterSpacing: "-0.02em" }}>
                    Підключіть Google Ads акаунт
                </h1>
                <p className="mt-3 text-secondary-c">Це дозволить AutoAds читати дані вашої реклами. Витрачати бюджет можете тільки ви.</p>

                {/* ── Step 1 of page: Customer ID ─────────────────────────── */}
                <section className="mt-8 glass rounded-2xl p-7" data-testid="step1-customer-id-block">
                    <div className="flex items-center gap-3">
                        <StepBadge n={1} active />
                        <h2 className="font-display font-bold text-xl text-text-primary">Введіть ваш Google Ads Customer ID</h2>
                    </div>

                    <div className="mt-5">
                        <label className="block text-sm font-medium text-text-primary">Customer ID</label>
                        <input
                            value={customerId}
                            onChange={(e) => handleChange(e.target.value)}
                            placeholder="123-456-7890"
                            data-testid="customer-id-input"
                            disabled={!!savedCustomerId}
                            className="mt-2 w-full md:w-[300px] bg-surface border rounded-md px-4 py-3 font-mono text-text-primary outline-none transition disabled:opacity-70"
                            style={{
                                borderColor: customerId === "" ? "var(--border)" :
                                    valid ? "var(--success)" : "var(--error)",
                            }}
                        />
                        <p className="mt-2 text-xs text-faint-c font-mono">Формат: XXX-XXX-XXXX (10 цифр)</p>
                    </div>

                    {/* Collapsible help */}
                    <div className="mt-5 border border-border rounded-lg overflow-hidden" data-testid="customer-id-help">
                        <button
                            type="button"
                            onClick={() => setHelpOpen((v) => !v)}
                            className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-surface-hover/30 transition"
                            data-testid="customer-id-help-toggle"
                        >
                            <span className="text-sm text-text-primary inline-flex items-center gap-2">
                                <Info size={14} className="text-primary-c" /> Де знайти Customer ID?
                            </span>
                            <ChevronDown size={14} className={`text-secondary-c transition-transform ${helpOpen ? "rotate-180" : ""}`} />
                        </button>
                        {helpOpen && (
                            <div className="px-4 py-4 border-t border-border bg-surface-hover/20 space-y-3" data-testid="customer-id-help-body">
                                <p className="text-sm text-text-primary/90 leading-relaxed">
                                    Відкрийте{" "}
                                    <a href="https://ads.google.com" target="_blank" rel="noopener noreferrer" className="text-primary-c hover:underline inline-flex items-center gap-1">
                                        ads.google.com <ExternalLink size={11} />
                                    </a>{" "}
                                    і подивіться у правий верхній кут — біля вашого email буде 10-значний номер у форматі <span className="font-mono text-primary-c">XXX-XXX-XXXX</span>. Це і є ваш Customer ID.
                                </p>
                                <img
                                    src={CUSTOMER_ID_SCREENSHOT}
                                    alt="Де знайти Customer ID у Google Ads"
                                    className="w-full rounded-md border border-border"
                                    data-testid="customer-id-screenshot"
                                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                                />
                            </div>
                        )}
                    </div>

                    {!savedCustomerId && (
                        <>
                            <label className="flex items-start gap-3 mt-6 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={consent}
                                    onChange={(e) => setConsent(e.target.checked)}
                                    data-testid="customer-id-consent"
                                    className="mt-1 accent-primary"
                                />
                                <span className="text-sm text-secondary-c">
                                    Це мій власний акаунт Google Ads. Авторизую AutoAds на читання даних реклами.
                                </span>
                            </label>

                            {error && (
                                <div className="mt-3 text-sm text-error-c font-mono" data-testid="step1-save-error">{error}</div>
                            )}

                            <div className="mt-6">
                                <button
                                    onClick={handleSubmit}
                                    disabled={!valid || !consent || saving}
                                    data-testid="step1-save-btn"
                                    className={`btn-primary ${(!valid || !consent || saving) ? "opacity-50 cursor-not-allowed" : "pulse-glow"}`}
                                >
                                    {saving && <Loader2 size={16} className="animate-spin" />}
                                    Зберегти і продовжити
                                </button>
                            </div>
                        </>
                    )}

                    {savedCustomerId && (
                        <div
                            className="mt-5 flex items-start gap-3 p-3 rounded-lg"
                            style={
                                isProblemStatus
                                    ? { background: "rgba(251,113,133,0.08)", border: "1px solid rgba(251,113,133,0.3)" }
                                    : { background: "rgba(45,212,191,0.08)", border: "1px solid rgba(45,212,191,0.2)" }
                            }
                            data-testid="step1-saved-banner"
                        >
                            {isProblemStatus ? (
                                <Info size={16} className="text-error-c mt-0.5 shrink-0" />
                            ) : (
                                <CheckCircle2 size={16} className="text-success-c mt-0.5 shrink-0" />
                            )}
                            <div className="flex-1 flex items-center justify-between gap-3 flex-wrap">
                                <div className="text-sm text-text-primary/90">
                                    {status === "expired" && (
                                        <>
                                            Запит для <span className="font-mono text-primary-c">{formatId(savedCustomerId)}</span> вичерпано (понад 7 днів без підтвердження).
                                            Натисніть <span className="font-semibold">«Змінити»</span> і введіть Customer ID повторно.
                                        </>
                                    )}
                                    {status === "rejected_conflict" && (
                                        <>
                                            Customer ID <span className="font-mono text-primary-c">{formatId(savedCustomerId)}</span> уже підключений іншим користувачем AutoAds.
                                            Введіть свій власний ID або зверніться до підтримки.
                                        </>
                                    )}
                                    {!isProblemStatus && (
                                        <>Customer ID <span className="font-mono text-primary-c">{formatId(savedCustomerId)}</span> збережено.</>
                                    )}
                                </div>
                                <button
                                    onClick={() => {
                                        setSavedCustomerId("");
                                        setCustomerId("");
                                        setStatus("idle");
                                        setError("");
                                    }}
                                    className="text-xs font-mono text-secondary-c hover:text-text-primary underline-offset-2 hover:underline transition"
                                    data-testid="customer-id-edit-btn"
                                >
                                    Змінити
                                </button>
                            </div>
                        </div>
                    )}
                </section>

                {/* ── Step 2 of page: Accept invitation ────────────────────── */}
                {showInviteBlock && (
                    <section className="mt-6 glass rounded-2xl p-7" data-testid="step1-invite-block">
                        <div className="flex items-center gap-3">
                            <StepBadge n={2} active={!canContinue} done={canContinue} />
                            <h2 className="font-display font-bold text-xl text-text-primary">Прийміть запрошення від AutoAds</h2>
                        </div>

                        <div
                            className="mt-4 flex items-start gap-3 p-4 rounded-lg"
                            style={{ background: "rgba(109,94,248,0.08)", border: "1px solid rgba(109,94,248,0.18)" }}
                        >
                            <Info size={16} className="text-primary-c mt-0.5 shrink-0" />
                            <p className="text-sm text-text-primary/90">
                                <span className="font-semibold">Ми надіслали запрошення</span> на адміністрування вашого акаунта з рівнем доступу <span className="font-semibold">«Лише читання»</span>. Прийміть його за 3 кроки:
                            </p>
                        </div>

                        <ol className="mt-5 space-y-4" data-testid="invite-steps">
                            <li className="flex gap-3 items-start">
                                <span className="font-mono text-xs text-primary-c shrink-0 w-6 mt-0.5">1.</span>
                                <div className="text-sm text-text-primary/90">
                                    Увійдіть у Google Ads:{" "}
                                    <a
                                        href="https://ads.google.com"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1 ml-1 px-2.5 py-1 rounded-md border border-primary/30 bg-primary/10 text-primary-c hover:bg-primary/20 transition"
                                        data-testid="ads-link"
                                    >
                                        ads.google.com <ExternalLink size={11} />
                                    </a>
                                </div>
                            </li>
                            <li className="flex gap-3 items-start">
                                <span className="font-mono text-xs text-primary-c shrink-0 w-6 mt-0.5">2.</span>
                                <div className="text-sm text-text-primary/90">
                                    Зліва оберіть вкладку <span className="font-mono text-text-primary">«Адміністратор»</span> →{" "}
                                    <span className="font-mono text-text-primary">«Доступ і безпека»</span> →{" "}
                                    <span className="font-mono text-text-primary">«Адміністратори»</span>. Згори побачите табличку <span className="font-mono text-text-primary">«Запит на зв'язування»</span>.
                                </div>
                            </li>
                            <li className="flex gap-3 items-start">
                                <span className="font-mono text-xs text-primary-c shrink-0 w-6 mt-0.5">3.</span>
                                <div className="text-sm text-text-primary/90">
                                    Натисніть <span className="font-mono text-success-c font-semibold">«Прийняти»</span>.
                                </div>
                            </li>
                        </ol>

                        {/* Status block */}
                        <div className="mt-7 pt-6 border-t border-border">
                            {status === "linked" ? (
                                <div
                                    className="flex items-center gap-3 p-4 rounded-lg"
                                    style={{ background: "rgba(45,212,191,0.1)", border: "1px solid rgba(45,212,191,0.3)" }}
                                    data-testid="invite-status-linked"
                                >
                                    <CheckCircle2 size={20} className="text-success-c shrink-0" />
                                    <div className="text-sm text-text-primary">
                                        <span className="font-semibold">Готово!</span> Ваш акаунт підключено до AutoAds. Можете продовжити онбординг.
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div
                                        className="flex items-center gap-3 p-4 rounded-lg"
                                        style={{ background: "rgba(251,191,36,0.08)", border: "1px solid rgba(251,191,36,0.25)" }}
                                        data-testid="invite-status-pending"
                                    >
                                        <Clock size={18} className="text-warning-c shrink-0 animate-pulse" />
                                        <div className="text-sm text-text-primary/90">
                                            <span className="font-mono">⏳ Очікуємо підтвердження…</span>
                                            <span className="ml-2 text-faint-c text-xs font-mono">(перевіряємо кожні 5 хвилин)</span>
                                        </div>
                                    </div>

                                    {error && (
                                        <div className="mt-3 text-sm text-error-c font-mono" data-testid="invite-check-error">{error}</div>
                                    )}

                                    <div className="mt-4">
                                        <button
                                            onClick={handleCheckNow}
                                            disabled={checking}
                                            className="btn-ghost"
                                            data-testid="invite-check-btn"
                                        >
                                            {checking ? <Loader2 size={14} className="animate-spin" /> : <RotateCw size={14} />}
                                            Я вже прийняв — перевірити зараз
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </section>
                )}

                {/* ── Continue / Skip buttons ──────────────────────────────── */}
                <div className="mt-8 flex justify-end items-center gap-3">
                    <button
                        type="button"
                        onClick={handleSkip}
                        data-testid="step1-skip-btn"
                        className="text-secondary-c hover:text-text-primary text-sm underline-offset-4 hover:underline transition-colors"
                        title="Продовжити без підключення Google Ads (з обмеженим функціоналом)"
                    >
                        Пропустити поки
                    </button>
                    <button
                        onClick={handleContinue}
                        disabled={!canContinue}
                        data-testid="step1-next-btn"
                        className={`btn-primary ${!canContinue ? "opacity-50 cursor-not-allowed" : "pulse-glow"}`}
                        title={!canContinue ? "Спочатку прийміть запрошення в Google Ads" : ""}
                    >
                        Далі <ArrowRight size={16} />
                    </button>
                </div>
            </div>
        </OnboardingShell>
    );
}

/* ───────────── helpers ───────────── */
function formatId(v) {
    const digits = String(v).replace(/\D/g, "").slice(0, 10);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return digits.slice(0, 3) + "-" + digits.slice(3);
    return digits.slice(0, 3) + "-" + digits.slice(3, 6) + "-" + digits.slice(6);
}

const StepBadge = ({ n, active, done }) => (
    <div
        className={`flex items-center justify-center w-8 h-8 rounded-full text-xs font-mono font-semibold ${
            done ? "bg-success text-background"
              : active ? "bg-primary text-white"
              : "bg-surface-hover text-secondary-c border border-border"
        }`}
        data-testid={`step1-badge-${n}`}
    >
        {done ? <CheckCircle2 size={14} /> : n}
    </div>
);
